import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import FadeIn from "@/components/FadeIn";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Brand } from "@/types/photos";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setBrand, isAuthenticated } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  // Helper function to transform database data to Brand type
  const transformToBrand = (data: any): Brand => {
    return {
      id: data.id,
      name: data.name,
      email: data.email,
      password: data.password,
      createdAt: data.created_at,
      uniqueUploadSlug: data.unique_upload_slug,
      storeCredit: data.store_credit,
      shopify_store_url: data.shopify_store_url,
      shopify_api_key: data.shopify_api_key,
      shopify_api_secret: data.shopify_api_secret,
      shopify_access_token: data.shopify_access_token,
      klaviyo_api_key: data.klaviyo_api_key,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError(null);
    
    try {
      console.log("Attempting login with:", email);
      
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (authError) {
        console.error("Authentication error:", authError);
        setAuthError(authError.message);
        throw authError;
      }

      if (!authData.user || !authData.session) {
        console.error("Authentication successful but no user or session returned");
        setAuthError("Login successful but session could not be established.");
        throw new Error("No user or session returned");
      }

      // Fetch brand data from the database
      const { data: brandData, error: brandError } = await supabase
        .from('brands')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (brandError) {
        console.error("Error fetching brand data:", brandError);
        setAuthError("Error fetching brand data");
        throw brandError;
      }

      if (!brandData) {
        console.error("No brand data found for user");
        setAuthError("No brand data found");
        throw new Error("No brand data found");
      }

      // Transform and set the brand data
      const transformedBrand = transformToBrand(brandData);
      setBrand(transformedBrand);

      // Reset form
      setEmail("");
      setPassword("");
      
      toast({
        title: "Logged in successfully",
        description: "Welcome back!",
      });
      
      // Navigate to dashboard after successful login
      navigate("/dashboard");
      
    } catch (error) {
      console.error("Login error:", error);
      toast({
        title: "Login failed",
        description: error instanceof Error ? error.message : "Please check your credentials and try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <FadeIn>
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center">Brand Login</CardTitle>
              <CardDescription className="text-center">
                Enter your credentials to sign in to your brand account
              </CardDescription>
            </CardHeader>
            <CardContent>
              {authError && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                  <p><strong>Error:</strong> {authError}</p>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="your@email.com" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input 
                    id="password" 
                    type="password" 
                    placeholder="••••••••" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required 
                  />
                </div>
                <div className="text-right">
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "Signing in..." : "Log in"}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex justify-center">
              <p className="text-sm text-muted-foreground">
                Don't have a brand account?{" "}
                <Link to="/register" className="text-primary hover:underline">
                  Sign up
                </Link>
              </p>
            </CardFooter>
          </Card>
        </FadeIn>
      </div>
      <Footer />
    </>
  );
};

export default Login;
